// Phase 1 (iter1): AppHeader — global search + Entry/Article count + controls
// counts は admin API でなく concept search の total を使う (レビュー M1)
import { useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'
import { useTheme } from '../theme'
import { useGlobalSearchQuery } from '../hooks/useGlobalSearchQuery'

interface AppHeaderProps {
  onSearch?: (q: string) => void  // Enter で Explore Lens 自動遷移
  onResetLayout?: () => void
  onLogout?: () => void
}

interface Counts { entries: number | null; articles: number | null }

export function AppHeader({ onSearch, onResetLayout, onLogout }: AppHeaderProps) {
  const { theme, toggle: toggleTheme } = useTheme()
  const [counts, setCounts] = useState<Counts>({ entries: null, articles: null })
  const [q, setQ] = useGlobalSearchQuery()

  useEffect(() => {
    let cancelled = false
    Promise.all([
      apiFetch<{ total?: number }>('/api/entries/search', { query: { limit: 1 } }).catch(() => null),
      apiFetch<{ total?: number }>('/api/articles/search', { query: { limit: 1 } }).catch(() => null),
    ]).then(([e, a]) => {
      if (cancelled) return
      setCounts({ entries: e?.total ?? null, articles: a?.total ?? null })
    })
    return () => { cancelled = true }
  }, [])

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter' && q.trim() && onSearch) {
      onSearch(q.trim())
      // ※ クリアしない: Header と Explore sidebar で同じ値を共有し続ける
    }
  }

  return (
    <header className="h-12 flex-shrink-0 border-b border-border-subtle bg-bg-surface flex items-center px-3 sm:px-4 gap-2 sm:gap-3 z-10">
      {/* Search */}
      <input
        type="search"
        value={q}
        onChange={e => setQ(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search… (Enter)"
        aria-label="Global search"
        className="flex-1 min-w-0 sm:max-w-xs px-3 py-1.5 bg-bg-base border border-border-subtle rounded text-sm text-text-base placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
      />

      {/* Counts — モバイルでは超コンパクト */}
      <div className="hidden sm:block flex-1 text-center text-xs text-text-subtle select-none" aria-live="polite">
        {counts.entries !== null && counts.articles !== null && (
          <span>
            Entries <strong className="text-text-base font-medium">{counts.entries}</strong>
            <span className="mx-2 opacity-40">·</span>
            Articles <strong className="text-text-base font-medium">{counts.articles}</strong>
          </span>
        )}
      </div>
      {/* モバイル用コンパクト counts (E nn / A nn) */}
      <div className="sm:hidden text-[10px] text-text-subtle font-mono flex-shrink-0 leading-tight select-none" aria-live="polite">
        {counts.entries !== null && counts.articles !== null && (
          <>
            <div>E <span className="text-text-base">{counts.entries}</span></div>
            <div>A <span className="text-text-base">{counts.articles}</span></div>
          </>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {onResetLayout && (
          <button
            type="button"
            onClick={onResetLayout}
            title="Reset layout (panel widths)"
            className="text-xs px-2 py-1 rounded text-text-subtle hover:bg-bg-overlay hover:text-text-base transition"
          >
            ⊞
          </button>
        )}
        <button
          type="button"
          onClick={toggleTheme}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          className="text-xs px-2 py-1 rounded text-text-subtle hover:bg-bg-overlay hover:text-text-base transition"
        >
          {theme === 'light' ? '🌙' : '☀'}
        </button>
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            title="Logout"
            aria-label="Logout"
            className="text-xs px-2 py-1 rounded text-text-subtle hover:bg-bg-overlay hover:text-text-base transition"
          >
            <span className="hidden sm:inline">Logout</span>
            <span className="sm:hidden">↪</span>
          </button>
        )}
      </div>
    </header>
  )
}
